<template>
  <div class="admin-view">
    <div class="page-header">
      <h1>Administration</h1>
      <p>Manage users, schools, and database backups</p>
    </div>

    <!-- Pending Users Alert (shown above tabs) -->
    <v-alert
      v-if="pendingUsers.length > 0"
      type="warning"
      variant="tonal"
      class="mb-4"
      closable
    >
      <div class="d-flex justify-space-between align-center">
        <div>
          <strong>{{ pendingUsers.length }} pending user approval{{ pendingUsers.length > 1 ? 's' : '' }}</strong>
        </div>
        <v-btn
          color="warning"
          size="small"
          @click="activeTab = 'users'"
        >
          Review Now
        </v-btn>
      </div>
    </v-alert>

    <!-- Tabs -->
    <v-card>
      <v-tabs v-model="activeTab" bg-color="primary">
        <v-tab value="users">
          <v-icon start>mdi-account-group</v-icon>
          Users
        </v-tab>
        <v-tab value="schools">
          <v-icon start>mdi-school</v-icon>
          Schools
        </v-tab>
        <v-tab value="volunteers">
          <v-icon start>mdi-account-heart</v-icon>
          Volunteers
        </v-tab>
        <v-tab value="notifier">
          <v-icon start>mdi-bell-ring</v-icon>
          Notifier
        </v-tab>
        <v-tab v-if="isAdmin" value="config">
          <v-icon start>mdi-cog</v-icon>
          Configuration
        </v-tab>
        <v-tab v-if="isAdmin" value="mailer">
          <v-icon start>mdi-email-alert</v-icon>
          SMTP
        </v-tab>
        <v-tab v-if="isAdmin" value="backups">
          <v-icon start>mdi-database</v-icon>
          Backups
        </v-tab>
      </v-tabs>

      <v-card-text>
        <v-window v-model="activeTab">
          <!-- Users Tab -->
          <v-window-item value="users">
            <PendingUsersCard
              :pending-users="pendingUsers"
              @approve="approveUser"
              @reject="rejectUser"
            />

            <!-- Users Table -->
            <UsersTable
              @create="openCreateUserModal"
              @edit="openEditUserModal"
              @delete="openDeleteUserModal"
            />
          </v-window-item>

          <!-- Schools Tab -->
          <v-window-item value="schools">
            <SchoolsTable />
          </v-window-item>

          <!-- Volunteers Tab -->
          <v-window-item value="volunteers">
            <VolunteersTable />
          </v-window-item>

          <!-- Backups Tab (Admin only) -->
          <v-window-item v-if="isAdmin" value="backups">
            <BackupsTable ref="backupsTableRef" />
          </v-window-item>

          <!-- Notifier Tab (Maintainer and Admin) -->
          <v-window-item value="notifier">
            <NotifierCard />
          </v-window-item>

          <!-- Configuration Tab (Admin only) -->
          <v-window-item v-if="isAdmin" value="config">
            <ConfigurationSettings />
          </v-window-item>

          <!-- SMTP Tab (Admin only) -->
          <v-window-item v-if="isAdmin" value="mailer">
            <MailerSettings />
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-card>

    <!-- Create User Modal -->
    <CreateUserModal
      :show="showCreateUserModal"
      :schools="schools"
      :submitting="submittingUser"
      :error="userError"
      @close="closeCreateUserModal"
      @submit="createUser"
    />

    <!-- Edit User Modal -->
    <EditUserModal
      :show="showEditUserModal"
      :user="editingUser"
      :schools="schools"
      :submitting="submittingUser"
      :error="userError"
      :is-self-edit="isSelfEdit"
      @close="closeEditUserModal"
      @submit="updateUser"
    />

    <!-- Delete User Confirmation Modal -->
    <v-dialog v-model="showDeleteUserModal" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-4">
            Are you sure you want to delete user <strong>{{ getUserDisplayName(userToDelete) }}</strong>?
          </p>
          <v-alert type="warning" variant="tonal">
            This action cannot be undone. The user will be permanently removed from the system.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeDeleteUserModal">Cancel</v-btn>
          <v-btn color="error" @click="confirmDeleteUser" :loading="deletingUser">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      location="top"
    >
      {{ snackbarMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '@/composables/api'
import { useAuthStore } from '@/store/auth'
import { useUserStore } from '@/store/users'
import SchoolsTable from '@/components/admin/SchoolsTable.vue'
import UsersTable from '@/components/admin/UsersTable.vue'
import BackupsTable from '@/components/admin/BackupsTable.vue'
import PendingUsersCard from '@/components/admin/PendingUsersCard.vue'
import CreateUserModal from '@/components/admin/CreateUserModal.vue'
import EditUserModal from '@/components/admin/EditUserModal.vue'
import MailerSettings from '@/components/admin/MailerSettings.vue'
import VolunteersTable from '@/components/admin/VolunteersTable.vue'
import NotifierCard from '@/components/admin/NotifierCard.vue'
import ConfigurationSettings from '@/components/admin/ConfigurationSettings.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()
const loading = ref(true)
const activeTab = ref('users')
const backupsTableRef = ref<any>(null)

// Check if current user is admin (not just maintainer)
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})

// User modals state
const showCreateUserModal = ref(false)
const showEditUserModal = ref(false)
const editingUser = ref<any>(null)
const submittingUser = ref(false)
const userError = ref('')
const schools = ref<any[]>([])

// Snackbar state
const showSnackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')
const showDeleteUserModal = ref(false)
const userToDelete = ref<any>(null)
const deletingUser = ref(false)

const pendingUsers = computed(() => {
  return userStore.users.filter((user: any) => user.status === 'pending' || user.role === 'pending')
})

const isSelfEdit = computed(() => {
  if (!editingUser.value || !authStore.authUser) return false
  const editingUserId = editingUser.value._id || editingUser.value.id
  const currentUserId = authStore.authUser._id || authStore.authUser.id
  return editingUserId === currentUserId
})

const loadUsers = async () => {
  loading.value = true
  try {
    await userStore.fetchUsers()
    console.log('Users loaded:', userStore.users.length, 'users')
  } catch (error) {
    console.error('Error loading users:', error)
  } finally {
    loading.value = false
  }
}

const approveUser = async (user: any) => {
  try {
    await api.patch(`/users/${user.id}/approve`)
    await loadUsers()
    // Update pending count in auth store
    await authStore.fetchPendingUsersCount()
    showNotification('User approved successfully', 'success')
  } catch (error) {
    console.error('Error approving user:', error)
    showNotification('Failed to approve user', 'error')
  }
}

const rejectUser = async (user: any) => {
  const displayName = getUserDisplayName(user)
  if (confirm(`Are you sure you want to reject ${displayName}?`)) {
    try {
      await api.delete(`/users/${user.id}`)
      await loadUsers()
      // Update pending count in auth store
      await authStore.fetchPendingUsersCount()
      showNotification('User rejected', 'success')
    } catch (error) {
      console.error('Error rejecting user:', error)
      showNotification('Failed to reject user', 'error')
    }
  }
}

const getUserDisplayName = (user: any): string => {
  if (user.username) {
    return user.username
  }
  const firstName = user.first_name || user.first || ''
  const lastName = user.last_name || user.last || ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || user.email || 'Unknown User'
}

const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  showSnackbar.value = true
}

// Load schools
const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

// Create user modal handlers
const openCreateUserModal = () => {
  userError.value = ''
  showCreateUserModal.value = true
}

const closeCreateUserModal = () => {
  showCreateUserModal.value = false
  userError.value = ''
}

const createUser = async (userData: any) => {
  submittingUser.value = true
  userError.value = ''

  try {
    await api.post('/users', userData)
    await loadUsers()
    closeCreateUserModal()
    showNotification('User created successfully!', 'success')
  } catch (error: any) {
    console.error('Error creating user:', error)
    if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else if (error.response?.status === 403) {
      userError.value = 'You do not have permission to create users.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to create user. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

// Edit user modal handlers
const openEditUserModal = (user: any) => {
  console.log('openEditUserModal called with:', user)
  if (!user) {
    console.error('openEditUserModal: user is null or undefined!')
    return
  }
  editingUser.value = user
  userError.value = ''
  showEditUserModal.value = true
  console.log('Modal opened, editingUser set to:', editingUser.value)
}

const closeEditUserModal = () => {
  showEditUserModal.value = false
  userError.value = ''
  // Don't clear editingUser immediately to avoid prop validation warning
  // The modal will reset its own state when it closes
  setTimeout(() => {
    editingUser.value = null
  }, 300) // Wait for modal close animation
}

const updateUser = async (userData: any) => {
  if (!editingUser.value) return

  submittingUser.value = true
  userError.value = ''

  try {
    const userId = editingUser.value._id || editingUser.value.id
    await api.put(`/users/${userId}`, userData)

    // Refetch all users to get the latest data
    await loadUsers()

    closeEditUserModal()
    showNotification('User updated successfully!', 'success')
  } catch (error: any) {
    console.error('Error updating user:', error)
    if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else if (error.response?.status === 403) {
      userError.value = 'You do not have permission to update users.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to update user. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

// Delete user modal handlers
const openDeleteUserModal = (user: any) => {
  console.log('openDeleteUserModal called with:', user)
  if (!user) {
    console.error('openDeleteUserModal: user is null or undefined!')
    return
  }

  // Prevent deleting yourself
  const currentUserId = authStore.authUser._id || authStore.authUser.id
  const targetUserId = user._id || user.id
  if (currentUserId === targetUserId) {
    showNotification('You cannot delete your own account', 'error')
    return
  }

  // Prevent deleting root users
  if (user.role === 'root') {
    showNotification('Root users cannot be deleted', 'error')
    return
  }

  userToDelete.value = user
  showDeleteUserModal.value = true
}

const closeDeleteUserModal = () => {
  showDeleteUserModal.value = false
  setTimeout(() => {
    userToDelete.value = null
  }, 300)
}

const confirmDeleteUser = async () => {
  if (!userToDelete.value) return

  deletingUser.value = true

  try {
    const userId = userToDelete.value._id || userToDelete.value.id
    await api.delete(`/users/${userId}`)
    await loadUsers()
    closeDeleteUserModal()
    showNotification('User deleted successfully!', 'success')
  } catch (error: any) {
    console.error('Error deleting user:', error)
    if (error.response?.status === 403) {
      showNotification(error.response?.data?.detail || 'You do not have permission to delete this user.', 'error')
    } else {
      showNotification(error.response?.data?.detail || 'Failed to delete user. Please try again.', 'error')
    }
  } finally {
    deletingUser.value = false
  }
}

// Valid tab names
const validTabs = ['users', 'schools', 'volunteers', 'notifier', 'config', 'mailer', 'backups']

// Initialize active tab from URL query parameter
const initializeTab = () => {
  const tabFromQuery = route.query.tab as string
  if (tabFromQuery && validTabs.includes(tabFromQuery)) {
    // Check if user has permission for admin-only tabs
    if ((tabFromQuery === 'mailer' || tabFromQuery === 'backups' || tabFromQuery === 'config') && !isAdmin.value) {
      activeTab.value = 'users'
    } else {
      activeTab.value = tabFromQuery
    }
  }
}

// Update URL query when tab changes
watch(activeTab, (newTab) => {
  if (route.query.tab !== newTab) {
    router.replace({ query: { ...route.query, tab: newTab } })
  }
})

// Watch for URL changes (e.g., browser back/forward)
watch(() => route.query.tab, (newTab) => {
  if (newTab && validTabs.includes(newTab as string)) {
    // Check if user has permission for admin-only tabs
    if ((newTab === 'mailer' || newTab === 'backups' || newTab === 'config') && !isAdmin.value) {
      activeTab.value = 'users'
    } else if (activeTab.value !== newTab) {
      activeTab.value = newTab as string
    }
  }
})

onMounted(() => {
  initializeTab()
  loadUsers()
  loadSchools()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.admin-view {
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.page-header p {
  color: var(--text-light, #666666);
  margin: 0;
}

@media (max-width: 768px) {
  .page-header h1 {
    font-size: 1.5rem;
  }
}
</style>
